/**
 * /apps 폰 화면의 순수 판단 (spec docs/superpowers/specs/2026-09-24-apps-mobile-design.md §1.4,
 * 계획 docs/superpowers/plans/2026-09-24-apps-mobile.md 의 R1). DOM·저장소·history 를 만지지
 * 않으므로 node --test 로 검사한다 (tests/unit/apps-mobile-logic.test.mjs).
 * 순서 배열은 언제나 오래된 것 → 최근 것.
 */
export const PHONE_LIVE_MAX = 3;
// 브라우저의 history.length 상한(Chromium·Firefox 50). 여기에 닿으면 프레임 안의 이동이 칸을 쌓아도
// length 가 더는 늘지 않아 "우리 칸 위에 아무것도 없다" 를 증명할 수 없다 — 그때는 되감지 않는 'push'.
export const HISTORY_LENGTH_CAP = 50;

// fillName: 문구 틀의 {name} 자리에 앱 이름을 글자 그대로 넣는다 — String.replace 에 문자열을 넘기면
// 이름 속의 $& · $' 가 치환 패턴으로 읽혀 라벨이 깨진다 (최종 리뷰 Minor 4).
export function fillName(pattern, name) {
    return String(pattern).split('{name}').join(name);
}

// recentOrder: 기록된 최근 순서를 지금 열린 앱에 맞춘다 — 닫힌 앱은 빠지고, 기록에 없는
// 열린 앱은 가장 오래된 쪽에 열린 순서대로 붙는다.
export function recentOrder(openIds, recent) {
    const open = new Set(openIds);
    const known = [];
    (recent || []).forEach((id) => {
        if (open.has(id) && !known.includes(id)) known.push(id);
    });
    return openIds.filter((id) => !known.includes(id)).concat(known);
}

// touch: id 를 가장 최근으로 옮긴다.
export function touch(order, id) {
    const rest = order.filter((x) => x !== id);
    return id ? rest.concat([id]) : rest;
}

// switchList: 전환 시트의 한 목록 — 열린 앱(최근 것부터) 다음에 나머지 서비스(런처 순서), 서비스마다 한 번.
// recentOpen 은 recentOrder 의 결과(오래된 것 → 최근 것)다.
export function switchList(allIds, recentOpen) {
    const seen = new Set();
    const out = [];
    const push = (id) => {
        if (id && !seen.has(id)) {
            seen.add(id);
            out.push(id);
        }
    };
    (recentOpen || []).slice().reverse().forEach(push);
    (allIds || []).forEach(push);
    return out;
}

// evictions: 상한을 넘는 만큼 가장 오래된 앱부터 — 지금 보이는 앱(keep)은 빼고.
export function evictions(order, keep, max = PHONE_LIVE_MAX) {
    const extra = order.length - max;
    if (extra <= 0) return [];
    return order.filter((id) => id !== keep).slice(0, extra);
}

// restoreSet: 새로고침 뒤 되살릴 앱 — 저장된 열린 앱 중 가장 최근 max 개(마지막으로 보던 앱은
// 가장 최근으로 친다)를 저장된 순서 그대로.
export function restoreSet(savedOpen, recent, active, max = PHONE_LIVE_MAX) {
    let order = recentOrder(savedOpen, recent);
    if (active && savedOpen.includes(active)) order = touch(order, active);
    const keep = new Set(order.slice(-max));
    return savedOpen.filter((id) => keep.has(id));
}

// leaveMode: ‹·「이 앱 닫기」가 런처로 갈 때 이력을 어떻게 다룰지.
//   'pop'     우리가 쌓은 앱 칸 바로 위에 아무것도 없다 → history.back() 으로 걷어 낸다
//   'replace' 이 문서에 들어온 뒤 이력이 그대로다(?open= 으로 바로 들어옴) → 제자리에서 런처 칸으로
//   'push'    프레임 안의 이동이 칸을 쌓았다 → 되감으면 앱이 보던 페이지를 잃으므로 런처 칸을 새로 쌓는다
export function leaveMode({ length, state, mine, baseLength }) {
    if (length >= HISTORY_LENGTH_CAP) return 'push';
    if (mine && length === mine.length && state && state.appsSeq === mine.seq) return 'pop';
    if (!mine && length === baseLength) return 'replace';
    return 'push';
}

// popDecision: popstate 에서 무엇을 보일지 — 보이는 것을 바꾸지 않을 칸은 같은 방향으로 한 칸 더 간다
// (아무것도 바뀌지 않는 뒤로가기를 만들지 않는다). 상태를 들고 다니지 않는다: 칸과 지금 보기만 본다.
//   열린 앱의 칸 → 그 앱 · 닫힌 앱의 칸 → 한 칸 더 · 런처 칸(상태 없는 첫 칸 포함) → 런처, 단 이미 런처를
//   보고 있으면 한 칸 더 (뒤로면 결국 /apps 를 떠난다). 방향은 칸의 appsSeq(쌓은 시각)로 안다 — 첫 칸은 0.
export function popDecision({ state, prevSeq, openIds, view }) {
    const st = state || {};
    const dir = (st.appsSeq || 0) < prevSeq ? 'back' : 'forward';
    if (st.appsView === 'app') {
        if (st.appsId && openIds.includes(st.appsId)) return { do: 'app', id: st.appsId };
        return { do: dir, skip: true };
    }
    if (view === 'launcher') return { do: dir, skip: true };
    return { do: 'launcher' };
}
